// Corrects a Holiday Club term's day dates when the form's row labels have typos. The labels (what parents see and
// what invoices print) stay as on the form; only the dates the calendar uses change. Saved in holidayTermDates.

import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import UndoIcon from "@mui/icons-material/Undo";
import { termDayDates, type HolidayTerm } from "../../shared/holidayClub";

const WEEKDAYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const weekdayOf = (iso: string) => new Date(`${iso}T12:00:00Z`).getUTCDay();
const labelWeekday = (label: string) => {
  const word = label.replace(/^\[/, "").trim().slice(0, 3).toLowerCase();
  return WEEKDAYS.indexOf(word);
};
const longDate = (iso: string) => new Date(`${iso}T12:00:00Z`).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

export const TermDatesDialog: React.FC<{
  term: HolidayTerm | null;
  corrections: { [label: string]: string };
  onClose: () => void;
  onSave: (dates: { [label: string]: string }) => Promise<void>;
}> = ({ term, corrections, onClose, onSave }) => {
  const [dates, setDates] = useState<{ [label: string]: string }>({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!term) return;
    setDates(Object.fromEntries(termDayDates(term, corrections).map((d) => [d.label, d.date ?? ""])));
    setSaving(false);
  }, [term?.id]);

  if (!term) return null;
  const rows = termDayDates(term, corrections);

  const save = async () => {
    setSaving(true);
    // Only differences from what the labels say are stored.
    const changed = Object.fromEntries(rows.filter((r) => dates[r.label] && dates[r.label] !== r.parsed).map((r) => [r.label, dates[r.label]]));
    await onSave(changed);
    onClose();
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Club dates · {term.name.replace(/^Registration form\s*-\s*/i, "")}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Dates are read from the form's day labels. Correct any that are wrong; the labels themselves stay as on the form.
        </Typography>
        <Stack spacing={1.25}>
          {rows.map((row) => {
            const value = dates[row.label] ?? "";
            const expected = labelWeekday(row.label);
            const mismatch = !!value && expected >= 0 && weekdayOf(value) !== expected;
            const changed = !!value && value !== row.parsed;
            return (
              <Stack key={row.label} direction="row" spacing={1.5} alignItems="center">
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={500} noWrap>{row.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {row.parsed ? `read as ${longDate(row.parsed)}` : "could not be read"}
                  </Typography>
                </Box>
                {changed && <Chip size="small" color="info" label="Corrected" />}
                {!value && <Chip size="small" color="error" label="No date" />}
                <TextField type="date" size="small" value={value} error={mismatch || !value}
                  helperText={mismatch ? "weekday doesn't match the label" : " "}
                  onChange={(e) => setDates({ ...dates, [row.label]: e.target.value })} sx={{ width: 190 }} />
                <Tooltip title="Back to the date read from the label">
                  <span>
                    <IconButton size="small" disabled={!changed} onClick={() => setDates({ ...dates, [row.label]: row.parsed ?? "" })}>
                      <UndoIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            );
          })}
        </Stack>
        {rows.some((r) => r.parsed && r.date && r.parsed !== r.date) && (
          <Alert severity="info" sx={{ mt: 2 }}>Corrected dates show on the calendar instead of what the label says.</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving}>Save dates</Button>
      </DialogActions>
    </Dialog>
  );
};
